import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  CheckCircle2,
  Clock3,
  Copy,
  History,
  LogOut,
  MapPinPlus,
  Pause,
  Play,
  RefreshCw,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { format, formatDistanceToNowStrict } from "date-fns";
import { th } from "date-fns/locale";
import type { Bootstrap, Member, Place, TrailPoint, Visit } from "../types";

export function Sheet({
  title,
  onClose,
  children,
  className = "",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className={`bottom-sheet ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <span className="sheet-handle" />
        <header>
          <h2>{title}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="ปิด">
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

const placeKinds = [
  { value: "home", emoji: "🏠", label: "บ้าน" },
  { value: "work", emoji: "💼", label: "ที่ทำงาน" },
  { value: "school", emoji: "🎒", label: "โรงเรียน" },
  { value: "food", emoji: "🍜", label: "ร้านอาหาร" },
  { value: "favorite", emoji: "🍬", label: "ที่โปรด" },
  { value: "other", emoji: "📍", label: "อื่นๆ" },
] as const;

export function AddPlaceSheet({
  coordinates,
  onClose,
  onSave,
}: {
  coordinates: { latitude: number; longitude: number };
  onClose: () => void;
  onSave: (input: {
    name: string;
    category: string;
    emoji: string;
    latitude: number;
    longitude: number;
    radius: number;
  }) => Promise<void>;
}) {
  const [kind, setKind] = useState<(typeof placeKinds)[number]>(placeKinds[4]);
  const [radius, setRadius] = useState(80);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      await onSave({
        name: String(data.get("name") ?? ""),
        category: kind.value,
        emoji: kind.emoji,
        radius,
        ...coordinates,
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet title="จำสถานที่นี้" onClose={onClose}>
      <form className="place-form" onSubmit={submit}>
        <div className="place-coordinate">
          <MapPinPlus size={18} />
          <span>
            จุดบนแผนที่
            <small>
              {coordinates.latitude.toFixed(5)}, {coordinates.longitude.toFixed(5)}
            </small>
          </span>
        </div>
        <label>
          <span>ชื่อสถานที่</span>
          <input name="name" autoFocus placeholder="เช่น บ้านคุณแม่" required minLength={2} />
        </label>
        <fieldset>
          <legend>ประเภท</legend>
          <div className="place-kind-grid">
            {placeKinds.map((item) => (
              <button
                type="button"
                key={item.value}
                className={kind.value === item.value ? "active" : ""}
                onClick={() => setKind(item)}
              >
                <span>{item.emoji}</span>
                <small>{item.label}</small>
              </button>
            ))}
          </div>
        </fieldset>
        <label className="radius-field">
          <span>
            ตรวจว่า “มาถึงแล้ว” ภายใน
            <b>{radius} เมตร</b>
          </span>
          <input
            type="range"
            min={30}
            max={300}
            step={10}
            value={radius}
            onChange={(event) => setRadius(Number(event.target.value))}
          />
        </label>
        {error && <div className="form-error">{error}</div>}
        <button className="primary-button" disabled={saving}>
          {saving ? "กำลังบันทึก…" : "บันทึกสถานที่"}
        </button>
      </form>
    </Sheet>
  );
}

export function PlaceDetailSheet({
  place,
  onClose,
  onCheckIn,
}: {
  place: Place;
  onClose: () => void;
  onCheckIn: () => Promise<void>;
}) {
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState("");

  async function checkIn() {
    setChecking(true);
    setMessage("");
    try {
      await onCheckIn();
      setMessage("เช็กอินแล้ว — ทุกคนในกลุ่มเห็นทันที");
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "เช็กอินไม่สำเร็จ");
    } finally {
      setChecking(false);
    }
  }

  return (
    <Sheet title={place.name} onClose={onClose}>
      <div className="place-detail">
        <div className="place-hero-emoji">{place.emoji}</div>
        <div className="place-stats">
          <span><b>{place.visitCount ?? 0}</b><small>ครั้งที่มา</small></span>
          <span>
            <b>{place.radius} ม.</b>
            <small>รัศมีตรวจจับ</small>
          </span>
        </div>
        {place.lastVisitedAt ? (
          <div className="soft-info">
            <CheckCircle2 size={18} />
            เคยมาล่าสุด{" "}
            {formatDistanceToNowStrict(new Date(place.lastVisitedAt), {
              addSuffix: true,
              locale: th,
            })}
          </div>
        ) : (
          <div className="soft-info neutral">
            <History size={18} /> ยังไม่มีใครเคยมาที่นี่
          </div>
        )}
        {message && <div className="inline-message">{message}</div>}
        <button className="primary-button" disabled={checking} onClick={checkIn}>
          <CheckCircle2 size={19} />
          {checking ? "กำลังยืนยัน…" : "ฉันมาถึงแล้ว"}
        </button>
        <p className="microcopy">ระบบจะตรวจตำแหน่งก่อนเช็กอินเพื่อความแม่นยำ</p>
      </div>
    </Sheet>
  );
}

export function MemberSheet({
  member,
  trail,
  onClose,
  onLoadTrail,
  onFocus,
}: {
  member: Member;
  trail: TrailPoint[];
  onClose: () => void;
  onLoadTrail: (hours: number) => Promise<void>;
  onFocus: () => void;
}) {
  const [hours, setHours] = useState(24);
  const latest = member.capturedAt ? new Date(member.capturedAt) : null;

  return (
    <Sheet title={member.name} onClose={onClose}>
      <div className="member-detail">
        <div className="member-detail-head">
          <span
            className="large-avatar"
            style={{ "--avatar-color": member.avatarColor } as React.CSSProperties}
          >
            {member.name.slice(0, 2).toUpperCase()}
          </span>
          <span>
            <b>{member.shareLocation ? "กำลังแชร์ตำแหน่ง" : "พักการแชร์อยู่"}</b>
            <small>
              {latest
                ? `อัปเดต ${formatDistanceToNowStrict(latest, { addSuffix: true, locale: th })}`
                : "ยังไม่มีตำแหน่ง"}
            </small>
          </span>
        </div>
        <div className="detail-metrics">
          <span>
            <small>ความแม่น</small>
            <b>{member.accuracy ? `±${Math.round(member.accuracy)} ม.` : "—"}</b>
          </span>
          <span>
            <small>ความเร็ว</small>
            <b>{member.speed != null ? `${Math.round(member.speed * 3.6)} กม./ชม.` : "—"}</b>
          </span>
          <span>
            <small>แบตเตอรี่</small>
            <b>{member.battery != null ? `${Math.round(member.battery * 100)}%` : "—"}</b>
          </span>
        </div>
        <div className="history-range">
          {[6, 24, 72].map((value) => (
            <button
              type="button"
              key={value}
              className={hours === value ? "active" : ""}
              onClick={() => {
                setHours(value);
                void onLoadTrail(value);
              }}
            >
              {value < 24 ? `${value} ชม.` : `${value / 24} วัน`}
            </button>
          ))}
        </div>
        <div className="trail-summary">
          <History size={18} />
          <span><b>{trail.length}</b><small>จุดเส้นทางในช่วงนี้</small></span>
        </div>
        <button className="primary-button" onClick={onFocus}>
          ดูบนแผนที่
        </button>
      </div>
    </Sheet>
  );
}

export function TimelineView({ visits }: { visits: Visit[] }) {
  const grouped = visits.reduce<Record<string, Visit[]>>((result, visit) => {
    const day = format(new Date(visit.arrivedAt), "yyyy-MM-dd");
    result[day] = [...(result[day] ?? []), visit];
    return result;
  }, {});

  return (
    <section className="tab-page timeline-page">
      <header className="page-title">
        <span className="eyebrow">ร่องรอยที่จำได้</span>
        <h1>ไปไหนมากันบ้าง</h1>
        <p>ระบบบันทึกเมื่อเข้าใกล้สถานที่ที่จำไว้โดยอัตโนมัติ</p>
      </header>
      {Object.keys(grouped).length === 0 ? (
        <div className="empty-state">
          <span>🍬</span>
          <h2>ยังไม่มีเรื่องราวบนแผนที่</h2>
          <p>เพิ่มสถานที่โปรด แล้ว Candy tracker จะช่วยจำว่าใครเคยไปมาแล้ว</p>
        </div>
      ) : (
        <div className="timeline-groups">
          {Object.entries(grouped).map(([day, dayVisits]) => (
            <section key={day}>
              <h2>
                {format(new Date(`${day}T12:00:00`), "EEEE d MMMM", { locale: th })}
              </h2>
              <div className="timeline-list">
                {dayVisits.map((visit) => (
                  <article key={visit.id} className="timeline-item">
                    <span className="timeline-emoji">{visit.emoji}</span>
                    <span className="timeline-copy">
                      <b>{visit.userName} ถึง {visit.placeName}</b>
                      <small>
                        {format(new Date(visit.arrivedAt), "HH:mm น.")}
                        {" · "}
                        {visit.leftAt
                          ? `${Math.max(1, Math.round(visit.durationSeconds / 60))} นาที`
                          : "อยู่ที่นี่ตอนนี้"}
                      </small>
                    </span>
                    {!visit.leftAt && <i className="live-dot" />}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </section>
  );
}

export function PlacesView({
  places,
  onSelect,
  onAddCurrent,
}: {
  places: Place[];
  onSelect: (place: Place) => void;
  onAddCurrent: () => void;
}) {
  return (
    <section className="tab-page places-page">
      <header className="page-title page-title-row">
        <span>
          <span className="eyebrow">พื้นที่ปลอดภัยของเรา</span>
          <h1>สถานที่ที่จำไว้</h1>
        </span>
        <button type="button" className="round-add" onClick={onAddCurrent} aria-label="เพิ่มสถานที่">
          <MapPinPlus size={22} />
        </button>
      </header>
      {places.length === 0 ? (
        <div className="empty-state">
          <span>📍</span>
          <h2>เริ่มจากสถานที่สำคัญ</h2>
          <p>เพิ่มบ้าน ที่ทำงาน หรือโรงเรียน แล้วระบบจะบอกทันทีเมื่อสมาชิกมาถึง</p>
          <button className="primary-button" onClick={onAddCurrent}>
            <MapPinPlus size={19} /> เพิ่มจากตำแหน่งฉัน
          </button>
        </div>
      ) : (
        <div className="place-card-grid">
          {places.map((place) => (
            <button type="button" className="place-card" key={place.id} onClick={() => onSelect(place)}>
              <span className="place-card-emoji">{place.emoji}</span>
              <span className="place-card-copy">
                <b>{place.name}</b>
                <small>
                  {place.lastVisitedAt
                    ? `ล่าสุด ${formatDistanceToNowStrict(new Date(place.lastVisitedAt), {
                        addSuffix: true,
                        locale: th,
                      })}`
                    : "ยังไม่เคยไป"}
                </small>
              </span>
              <span className="visit-count">{place.visitCount ?? 0} ครั้ง</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export function ProfileView({
  data,
  sharing,
  onToggleSharing,
  onRegenerateInvite,
  onClearHistory,
  onLogout,
}: {
  data: Bootstrap;
  sharing: boolean;
  onToggleSharing: () => Promise<void>;
  onRegenerateInvite: () => Promise<void>;
  onClearHistory: () => Promise<void>;
  onLogout: () => Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  async function copyCode() {
    await navigator.clipboard.writeText(data.space.inviteCode);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <section className="tab-page profile-page">
      <header className="profile-identity">
        <span className="profile-candy">🍬</span>
        <h1>{data.user.name}</h1>
        <p>{data.user.email}</p>
        <span className="role-pill">
          <ShieldCheck size={14} />
          {data.membership.role === "owner" ? "เจ้าของกลุ่ม" : "สมาชิก"}
        </span>
      </header>

      <div className="settings-group">
        <h2>การแชร์ตำแหน่ง</h2>
        <button className="setting-row" onClick={() => void onToggleSharing()}>
          <span className={`setting-icon ${sharing ? "pink" : "gray"}`}>
            {sharing ? <Play size={19} /> : <Pause size={19} />}
          </span>
          <span><b>{sharing ? "กำลังแชร์แบบเรียลไทม์" : "พักการแชร์อยู่"}</b><small>แตะเพื่อ{sharing ? "หยุดชั่วคราว" : "เริ่มแชร์"}</small></span>
          <i className={`toggle ${sharing ? "on" : ""}`}><span /></i>
        </button>
      </div>

      <div className="settings-group">
        <h2>ชวนเข้ากลุ่ม “{data.space.name}”</h2>
        <div className="invite-card">
          <small>รหัสเชิญ</small>
          <strong>{data.space.inviteCode}</strong>
          <button onClick={() => void copyCode()}>
            {copied ? <CheckCircle2 size={17} /> : <Copy size={17} />}
            {copied ? "คัดลอกแล้ว" : "คัดลอก"}
          </button>
        </div>
        {["owner", "admin"].includes(data.membership.role) && (
          <button className="text-action" onClick={() => void onRegenerateInvite()}>
            <RefreshCw size={16} /> สร้างรหัสเชิญใหม่
          </button>
        )}
      </div>

      <div className="settings-group">
        <h2>ข้อมูลและความเป็นส่วนตัว</h2>
        {confirmClear ? (
          <div className="confirm-card">
            <b>ลบเส้นทางของคุณทั้งหมด?</b>
            <p>ประวัติจุด GPS จะถูกลบถาวร แต่รายการเช็กอินยังอยู่</p>
            <div>
              <button onClick={() => setConfirmClear(false)}>ยกเลิก</button>
              <button className="danger" onClick={() => void onClearHistory()}>ลบข้อมูล</button>
            </div>
          </div>
        ) : (
          <button className="setting-row" onClick={() => setConfirmClear(true)}>
            <span className="setting-icon red"><Trash2 size={19} /></span>
            <span><b>ลบประวัติเส้นทางของฉัน</b><small>ลบจุด GPS ทั้งหมดทันที</small></span>
          </button>
        )}
        <button className="setting-row" onClick={() => void onLogout()}>
          <span className="setting-icon gray"><LogOut size={19} /></span>
          <span><b>ออกจากระบบ</b><small>ข้อมูลในกลุ่มจะไม่ถูกลบ</small></span>
        </button>
      </div>
      <footer className="profile-footer">
        <Clock3 size={15} /> เก็บประวัติเส้นทาง 30 วัน
        <span>Candy tracker 1.0</span>
      </footer>
    </section>
  );
}
